import ApiService, { IAjaxOptions } from '@app/Services/ApiService';
import CookieService from './CookieService';
import { PromiseCompletion } from '@app/Classes/PromiseCompletion';

class DownloadService {
    public async downloadFile<T>(url: string, params?: T, sourceFileName?: string | null, options?: IAjaxOptions) {
        const { data, headers } = await ApiService.get<Buffer>(url, params, { responseType: 'blob', ...options });

        let fileName = sourceFileName;
        if (!fileName) {
            fileName = '';
            const disposition = headers['content-disposition'];
            if (disposition && disposition.indexOf('attachment') !== -1) {
                const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                const matches = filenameRegex.exec(disposition);
                if (matches && matches[1]) {
                    fileName = matches[1].replace(/['"]/g, '');
                }
            }
        }

        const blob = new Blob([data]);
        await this.downloadBlob(blob, fileName);
    }

    public async downloadBlob(blob: Blob, fileName: string) {
        const objectUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        await link.click();
    }

    /**
        Method to avoid out-of-memory issues
        *
        https://github.com/Tampermonkey/tampermonkey/issues/1516
    */
    public downloadSafely(downloadCallback: () => Promise<void> | void, loader: PromiseCompletion) {
        const cookieName = 'download-marker';
        const defaultAttempts = 300;
        const timeout = 100;

        let attempts = defaultAttempts;
        let downloadTimer = 0;

        CookieService.expireCookie(cookieName);

        loader.subscribe(new Promise<void>((resolve, reject) => {
            void downloadCallback();

            downloadTimer = window.setInterval(() => {
                const token = CookieService.getCookie(cookieName);

                if (token || attempts === 0) {
                    window.clearInterval(downloadTimer);

                    if (attempts === 0) {
                        const seconds = defaultAttempts * timeout / 1000;
                        reject(`Failed to wait for file download for ${seconds} seconds`);
                    } else {
                        resolve();
                    }
                }

                attempts--;
            }, timeout);
        }));
    }
}

export const downloadService = new DownloadService();